import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { SessionSettingsService } from '../../services/session-settings.service';

// Slice
import { SessionSettingsSliceActions } from './session-settings.slice';

// Store entities
import { openNotification } from '../app-notifications/app-notifications.thunks';

// Models
import { IAppointmentSettings, IStoreSessionUpdates } from '../../interfaces/session-settings.interfaces';
import { TSessionType } from '../../types/settings.types';

export enum ESessionSettingsThunks {
  GetSessionAvailableAttributes = 'SESSION_SETTINGS/getSessionAvailableAttributes',
  GetStoreSessionAttributes = 'SESSION_SETTINGS/getStoreSessionAttributes',
  GetAvailableCallPrebookedTime = 'SESSION_SETTINGS/getAvailableCallPrebookedTime',
  GetAppointmentsSettings = 'SESSION_SETTINGS/getAppointmentsSettings',
  UpdateAppointmentsSettings = 'SESSION_SETTINGS/updateAppointmentsSettings',
  UpdateAvailableCallPrebookedTime = 'SESSION_SETTINGS/updateAvailableCallPrebookedTime',
  GetAvailableCorrespondenceTime = 'SESSION_SETTINGS/getAvailableCorrespondenceTime',
  UpdateAvailableCorrespondenceTime = 'SESSION_SETTINGS/updateAvailableCorrespondenceTime',
  CreateStoreSessionAttributes = 'SESSION_SETTINGS/createStoreSessionAttributes',
  UpdateStoreSessionAttributes = 'SESSION_SETTINGS/updateStoreSessionAttributes',
  DeleteStoreSessionAttributes = 'SESSION_SETTINGS/deleteStoreSessionAttributes',
  ClearSessionSettings = 'SESSION_SETTINGS/clearSessionSettings',
}

export const getSessionAvailableAttributes = createAsyncThunk(
  ESessionSettingsThunks.GetSessionAvailableAttributes,
  async (_, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getSessionAvailableAttributesRequest());
    const { data, error } = await SessionSettingsService.fetchSessionAvailableAttributes();
    if (!data) {
      dispatch(SessionSettingsSliceActions.getSessionAvailableAttributesRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(SessionSettingsSliceActions.getSessionAvailableAttributesRequestSuccess(data));
    return data;
  }
);

export const getStoreSessionAttributes = createAsyncThunk(
  ESessionSettingsThunks.GetStoreSessionAttributes,
  async (_, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequest());
    const { data, error } = await SessionSettingsService.fetchStoreSessionAttributes();
    if (!data) {
      dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequestSuccess(data));
    return data;
  }
);

export const getAvailableCallPrebookedTime = createAsyncThunk(
  ESessionSettingsThunks.GetAvailableCallPrebookedTime,
  async (_, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequest());
    const { data, error } = await SessionSettingsService.fetchAvailableCallPrebookedTime();
    if (!data) {
      dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequestSuccess(data));
    return data;
  }
);

export const getAppointmentsSettings = createAsyncThunk(
  ESessionSettingsThunks.GetAppointmentsSettings,
  async (_, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getAppointmentSettingsRequest());
    const { data, error } = await SessionSettingsService.fetchAppointmentsSettings();
    if (!data) {
      dispatch(SessionSettingsSliceActions.getAppointmentSettingsRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(SessionSettingsSliceActions.getAppointmentSettingsRequestSuccess(data));
    return data;
  }
);

export const updateAppointmentsSettings = createAsyncThunk(
  ESessionSettingsThunks.UpdateAppointmentsSettings,
  async ({ value, key }: { value: string | number, key: 'max_approval_hours' | 'max_user_cancellation_hours' }, { dispatch }) => {
    const { data, error } = await SessionSettingsService.updateAppointmentsSettings(value, key);
    if (!data) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, update confirmation time failed, try again!',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.updateAppointmentsSettings(data));
    return data;
  }
);

export const updateAvailableCallPrebookedTime = createAsyncThunk(
  ESessionSettingsThunks.UpdateAvailableCallPrebookedTime,
  async (call_prebooked_time: number | string, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequest());
    const { data, error } = await SessionSettingsService.updateAvailableCallPrebookedTime(call_prebooked_time );
    if (!data) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, update minutes failed, try again!',
        type: 'error'
      }));
      dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequestFailure(error ?? 'Something went wrong, update minutes failed, try again!'));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.getAvailableCallPrebookedTimeRequestSuccess(data));
    return data;
  }
);

export const getAvailableCorrespondenceTime = createAsyncThunk(
  ESessionSettingsThunks.GetAvailableCorrespondenceTime,
  async (_, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequest());
    const { data, error } = await SessionSettingsService.fetchAvailableCorrespondenceTime();
    if (!data) {
      dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequestSuccess(data));
    return data;
  }
);

export const updateAvailableCorrespondenceTime = createAsyncThunk(
  ESessionSettingsThunks.UpdateAvailableCorrespondenceTime,
  async (correspondence_message_time: number | string, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequest());
    const { data, error } = await SessionSettingsService.updateAvailableCorrespondenceTime(correspondence_message_time);
    if (!data) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, update correspondence failed, try again!',
        type: 'error'
      }));
      dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequestFailure(error ?? 'Something went wrong, update correspondence failed, try again!'));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.getAvailableCorrespondenceTimeRequestSuccess(data));
    return data;
  }
);

export const createStoreSessionAttributes = createAsyncThunk(
  ESessionSettingsThunks.CreateStoreSessionAttributes,
  async (session: IStoreSessionUpdates, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequest());
    const { session_setting, error } = await SessionSettingsService.createStoreSessionAttributes(session);
    if (!session_setting) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, create session failed, try again!',
        type: 'error'
      }));
      dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequestFailure(error ?? 'Something went wrong, create session failed, try again!'));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.createStoreSessionAttributes(session_setting));
    return session_setting;
  }
);

export const updateStoreSessionAttributes = createAsyncThunk(
  ESessionSettingsThunks.UpdateStoreSessionAttributes,
  async (session: IStoreSessionUpdates, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequest());
    const { session_setting, error } = await SessionSettingsService.updateStoreSessionAttributes(session);
    if (!session_setting) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, update session failed, try again!',
        type: 'error'
      }));
      dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequestFailure(error ?? 'Something went wrong, update session failed, try again!'));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.updateStoreSessionAttributes(session_setting));
    return session_setting;
  }
);

export const deleteStoreSessionAttributes = createAsyncThunk(
  ESessionSettingsThunks.DeleteStoreSessionAttributes,
  async ({ id, session_type }: { id: string | number, session_type: TSessionType }, { dispatch }) => {
    dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequest());
    const { message, error } = await SessionSettingsService.deleteStoreSessionAttributes(id);
    if (!message) {
      dispatch(openNotification({
        description: error ?? 'Something went wrong, delete session failed, try again!',
        type: 'error'
      }));
      dispatch(SessionSettingsSliceActions.getStoreSessionAttributesRequestFailure(error ?? 'Something went wrong, delete session failed, try again!'));
      return false;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(SessionSettingsSliceActions.deleteStoreSessionAttributes({
      id,
      session_type
    }));
    return true;
  }
);

export const clearPermissionSettings = createAsyncThunk(
  ESessionSettingsThunks.ClearSessionSettings,
  async (_, { dispatch }) => {
    return dispatch(SessionSettingsSliceActions.clearSessionSettings());
  }
);
